import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import SelectionChromeOverlay, {
  CANVAS_SELECTION_HANDLE_CLASS,
} from "../creativeTemplates/editor/SelectionChromeOverlay";
import { resizePixelBoxFromHandle } from "../creativeTemplates/editor/resizePixelBoxFromHandle";
import { useBoardOverlayFrame } from "../creativeTemplates/editor/useBoardOverlayFrame";
import {
  idsIntersectingMarquee,
  MARQUEE_MIN_SIZE_PX,
  marqueeOverlaySx,
  normalizeMarqueeRect,
} from "../creativeTemplates/editor/canvasMarquee";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  commitVideoEditorClipUpdates,
  setVideoEditorSelection,
} from "../../actions/videoEditor";
import {
  VIDEO_EDITOR_CLIP_COLORS,
  VIDEO_EDITOR_DEFAULT_COMPOSITION,
  VIDEO_EDITOR_TRACK_TYPE,
} from "./constants";
import {
  clampPixelBoxToRect,
  compositionPixelsToLayout,
  createFullCanvasClipLayout,
  getSourceAspectRatio,
  layoutToCompositionPixels,
  layoutToPercentStyle,
  repairLayoutAspectRatio,
} from "./clipLayout";
import {
  selectVideoEditorEdl,
  selectVideoEditorPlayhead,
  selectVideoEditorSelectedClipId,
  selectVideoEditorSelectedClipIds,
} from "./videoEditorSelectors";
import { useVideoEditorPlaybackTime } from "./videoEditorPlaybackClock";
import { clipHasCanvasLayout } from "./clipProperties";
import {
  getClipSelectionMode,
  isAdditiveLayerSelect,
  toSelectionModifiers,
  VIDEO_EDITOR_SELECTION_MODE,
} from "./clipSelection";
import { getVisualClipsAtPlayhead, getVisualLayerZIndex } from "./visualClips";
import { restoreDragItems } from "./visualLayerDrag";

const MIN_BOX_SIZE_PX = 24;
const IMAGE_RESIZE_HANDLES = ["nw", "ne", "sw", "se"];
const ASPECT_LOCK_CORNERS = new Set(IMAGE_RESIZE_HANDLES);

const getClipAspectRatio = (clip) =>
  getSourceAspectRatio({
    sourceWidth: clip?.sourceWidth,
    sourceHeight: clip?.sourceHeight,
  });

const toAspectLockedCorner = (corner) => {
  if (ASPECT_LOCK_CORNERS.has(corner)) {
    return corner;
  }

  switch (corner) {
    case "e":
      return "se";
    case "w":
      return "sw";
    case "n":
      return "ne";
    case "s":
      return "sw";
    default:
      return "se";
  }
};

const clientToCanvasPixels = (event, canvas) => {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) {
    return { x: 0, y: 0 };
  }

  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.offsetWidth,
    y: ((event.clientY - rect.top) / rect.height) * canvas.offsetHeight,
  };
};

const pixelBoxToPercentStyle = ({ box, stageWidth, stageHeight }) => ({
  left: `${(box.x / stageWidth) * 100}%`,
  top: `${(box.y / stageHeight) * 100}%`,
  width: `${(box.width / stageWidth) * 100}%`,
  height: `${(box.height / stageHeight) * 100}%`,
});

const isResizeHandleEvent = (event) =>
  Boolean(event.target?.closest?.(`.${CANVAS_SELECTION_HANDLE_CLASS}`));

const applyBoxStyle = (node, box, frame) => {
  if (!node || !box || !frame?.width) {
    return;
  }
  const style = pixelBoxToPercentStyle({
    box,
    stageWidth: frame.width,
    stageHeight: frame.height,
  });
  node.style.left = style.left;
  node.style.top = style.top;
  node.style.width = style.width;
  node.style.height = style.height;
  node.style.right = "auto";
  node.style.bottom = "auto";
};

const clearBoxStyle = (node) => {
  if (!node) {
    return;
  }
  node.style.left = "";
  node.style.top = "";
  node.style.width = "";
  node.style.height = "";
  node.style.right = "";
  node.style.bottom = "";
};

const resolveMovingIds = ({ clipId, selectedClipIds, additive }) => {
  if (additive) {
    return selectedClipIds.includes(clipId)
      ? selectedClipIds
      : [...selectedClipIds, clipId];
  }
  if (selectedClipIds.includes(clipId) && selectedClipIds.length > 1) {
    return selectedClipIds;
  }
  return [clipId];
};

const VideoEditorVisualLayer = memo(function VideoEditorVisualLayer({
  clip,
  layoutStyle,
  isPlaying,
  onMovePointerDown,
}) {
  return (
    <Box
      data-video-editor-visual-layer={clip.id}
      onPointerDown={
        isPlaying ? undefined : (event) => onMovePointerDown(event, clip)
      }
      sx={{
        position: "absolute",
        left: layoutStyle.left,
        top: layoutStyle.top,
        width: layoutStyle.width,
        height: layoutStyle.height,
        zIndex: getVisualLayerZIndex(clip),
        cursor: isPlaying ? "default" : "move",
        overflow: "hidden",
        pointerEvents: isPlaying ? "none" : "auto",
        bgcolor: "transparent",
        touchAction: "none",
      }}
    >
      {clip.type === VIDEO_EDITOR_TRACK_TYPE.IMAGE ? (
        <Box
          component="img"
          src={clip.sourceUrl}
          alt={clip.label || "Image layer"}
          draggable={false}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "contain",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      ) : null}
    </Box>
  );
});

const VideoEditorLayerOverlay = ({
  stageRef,
  isPlaying = false,
  onPauseForEdit = () => {},
}) => {
  const dispatch = useDispatchApp();
  const edl = useSelectorApp(selectVideoEditorEdl);
  const reduxPlayhead = useSelectorApp(selectVideoEditorPlayhead);
  const playhead = useVideoEditorPlaybackTime(reduxPlayhead);
  const selectedClipId = useSelectorApp(selectVideoEditorSelectedClipId);
  const selectedClipIds = useSelectorApp(selectVideoEditorSelectedClipIds);
  const overlayRef = useRef(null);
  const viewportRef = useRef(null);
  const activeDragRef = useRef(null);
  const rafRef = useRef(null);
  const marqueeRef = useRef(null);
  const [marquee, setMarquee] = useState(null);
  const frameRef = useRef({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });

  const frame = useBoardOverlayFrame({
    boardRef: stageRef,
    overlayRef,
    viewportRef,
    syncKey: isPlaying ? "playing" : playhead,
  });
  frameRef.current = frame;

  const isBoardMeasured = frame.width > 0 && frame.height > 0;
  const editBounds = useMemo(() => {
    if (!isBoardMeasured) {
      return null;
    }
    return { x: 0, y: 0, width: frame.width, height: frame.height };
  }, [frame.height, frame.width, isBoardMeasured]);

  const activeVisualClips = useMemo(
    () => getVisualClipsAtPlayhead({ edl, playhead }),
    [edl, playhead]
  );

  const getClipResolvedLayout = useCallback(
    (clip) =>
      repairLayoutAspectRatio({
        layout: clip.layout,
        sourceWidth: clip.sourceWidth,
        sourceHeight: clip.sourceHeight,
        compositionWidth:
          edl?.compositionWidth || VIDEO_EDITOR_DEFAULT_COMPOSITION.width,
        compositionHeight:
          edl?.compositionHeight || VIDEO_EDITOR_DEFAULT_COMPOSITION.height,
        fallbackLayout:
          clip?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO
            ? createFullCanvasClipLayout()
            : undefined,
      }),
    [edl?.compositionHeight, edl?.compositionWidth]
  );

  const getClipPixelBox = useCallback(
    (clip) => {
      if (!isBoardMeasured || !clip) {
        return null;
      }
      return layoutToCompositionPixels({
        layout: getClipResolvedLayout(clip),
        compositionWidth: frame.width,
        compositionHeight: frame.height,
      });
    },
    [frame.height, frame.width, getClipResolvedLayout, isBoardMeasured]
  );

  const applyDragDom = useCallback((items) => {
    const overlay = overlayRef.current;
    const currentFrame = frameRef.current;
    if (!overlay || !currentFrame.width || !items?.length) {
      return;
    }
    items.forEach((item) => {
      applyBoxStyle(
        overlay.querySelector(
          `[data-video-editor-visual-layer="${item.clipId}"]`
        ),
        item.box,
        currentFrame
      );
      applyBoxStyle(
        overlay.parentElement?.querySelector(
          `[data-video-editor-video-layer="${item.clipId}"]`
        ),
        item.box,
        currentFrame
      );
      const chrome = overlay.querySelector(
        `[data-template-chrome-id="${item.clipId}"]`
      );
      if (chrome) {
        chrome.style.left = `${currentFrame.left + item.box.x}px`;
        chrome.style.top = `${currentFrame.top + item.box.y}px`;
        chrome.style.width = `${Math.max(0, item.box.width)}px`;
        chrome.style.height = `${Math.max(0, item.box.height)}px`;
      }
    });
  }, []);

  const clearDragDom = useCallback((clipId) => {
    const overlay = overlayRef.current;
    if (!overlay || !clipId) {
      return;
    }
    ["data-video-editor-visual-layer", "data-template-chrome-id"].forEach(
      (attr) => {
        clearBoxStyle(overlay.querySelector(`[${attr}="${clipId}"]`));
      }
    );
  }, []);

  const getClipLayoutStyle = useCallback(
    (clip) => {
      if (!isBoardMeasured) {
        return null;
      }
      return layoutToPercentStyle(getClipResolvedLayout(clip));
    },
    [getClipResolvedLayout, isBoardMeasured]
  );

  const commitLayouts = useCallback(
    ({ items, displayWidth, displayHeight }) => {
      if (!displayWidth || !displayHeight || !editBounds || !items?.length) {
        return;
      }
      dispatch(
        commitVideoEditorClipUpdates({
          updates: items.map((item) => {
            const clip = edl?.clips?.byId?.[item.clipId];
            const aspectRatio = getClipAspectRatio(clip);
            return {
              clipId: item.clipId,
              updates: {
                layout: compositionPixelsToLayout({
                  ...clampPixelBoxToRect({
                    ...item.box,
                    rect: editBounds,
                    minSize: MIN_BOX_SIZE_PX,
                    inset: 0,
                    aspectRatio,
                  }),
                  compositionWidth: displayWidth,
                  compositionHeight: displayHeight,
                  aspectRatio,
                }),
              },
            };
          }),
        })
      );
    },
    [dispatch, editBounds, edl?.clips?.byId]
  );

  const selections = useMemo(() => {
    if (isPlaying || !isBoardMeasured || !editBounds) {
      return [];
    }
    return selectedClipIds
      .map((clipId) => {
        const clip = edl?.clips?.byId?.[clipId];
        if (!clipHasCanvasLayout(clip)) {
          return null;
        }
        if (!activeVisualClips.some((item) => item.id === clipId)) {
          return null;
        }
        const box = getClipPixelBox(clip);
        if (!box) {
          return null;
        }
        return {
          id: clip.id,
          x: box.x,
          y: box.y,
          width: box.width,
          height: box.height,
          color: VIDEO_EDITOR_CLIP_COLORS[clip.type],
          showHandles: clip.id === selectedClipId,
          handleCorners: IMAGE_RESIZE_HANDLES,
          showLabel: false,
          rotation: 0,
        };
      })
      .filter(Boolean);
  }, [
    activeVisualClips,
    edl?.clips?.byId,
    editBounds,
    getClipPixelBox,
    isPlaying,
    isBoardMeasured,
    selectedClipId,
    selectedClipIds,
  ]);

  const onHandlePointerDown = useCallback(
    ({ event, id, corner }) => {
      if (isPlaying) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      onPauseForEdit();
      const clip = edl?.clips?.byId?.[id];
      const board = stageRef.current;
      const originBox = clip ? getClipPixelBox(clip) : null;
      if (!clip || !board || !originBox) {
        return;
      }
      activeDragRef.current = {
        mode: "resize",
        items: [
          {
            clipId: id,
            originBox,
            box: originBox,
            aspectRatio: getClipAspectRatio(clip),
          },
        ],
        corner: toAspectLockedCorner(corner),
        startPoint: clientToCanvasPixels(event, board),
      };
      applyDragDom(activeDragRef.current.items);
      dispatch(setVideoEditorSelection({ clipId: id, preserveMulti: true }));
    },
    [
      applyDragDom,
      dispatch,
      edl?.clips?.byId,
      getClipPixelBox,
      isPlaying,
      onPauseForEdit,
      stageRef,
    ]
  );

  const onMovePointerDown = useCallback(
    (event, clip) => {
      if (isPlaying || isResizeHandleEvent(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      onPauseForEdit();
      const board = stageRef.current;
      if (!board) {
        return;
      }
      const modifiers = toSelectionModifiers(event);
      dispatch(
        setVideoEditorSelection({
          clipId: clip.id,
          event: modifiers,
          mode: getClipSelectionMode(modifiers),
          preserveMulti: true,
        })
      );
      const ids = resolveMovingIds({
        clipId: clip.id,
        selectedClipIds,
        additive: isAdditiveLayerSelect(event),
      }).filter((clipId) =>
        activeVisualClips.some((item) => item.id === clipId)
      );
      const items = (ids.length ? ids : [clip.id])
        .map((clipId) => {
          const target = edl?.clips?.byId?.[clipId];
          const box = getClipPixelBox(target);
          if (!box) {
            return null;
          }
          return {
            clipId,
            originBox: box,
            box,
            aspectRatio: getClipAspectRatio(target),
          };
        })
        .filter(Boolean);
      const point = clientToCanvasPixels(event, board);
      const primary = items.find((item) => item.clipId === clip.id) || items[0];
      activeDragRef.current = {
        mode: "move",
        items,
        grabOffset: primary
          ? {
              x: point.x - primary.originBox.x,
              y: point.y - primary.originBox.y,
            }
          : { x: 0, y: 0 },
        primaryClipId: clip.id,
      };
      applyDragDom(items);
    },
    [
      activeVisualClips,
      applyDragDom,
      dispatch,
      edl?.clips?.byId,
      getClipPixelBox,
      isPlaying,
      onPauseForEdit,
      selectedClipIds,
      stageRef,
    ]
  );

  const onOverlayPointerDown = useCallback(
    (event) => {
      if (isPlaying || event.button !== 0) {
        return;
      }
      if (
        event.target?.closest?.("[data-video-editor-visual-layer]") ||
        isResizeHandleEvent(event)
      ) {
        return;
      }
      const board = stageRef.current;
      if (!board) {
        return;
      }
      const point = clientToCanvasPixels(event, board);
      marqueeRef.current = {
        originX: point.x,
        originY: point.y,
        additive: isAdditiveLayerSelect(event),
      };
    },
    [isPlaying, stageRef]
  );

  useEffect(() => {
    const stopRaf = () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const finishActiveDrag = ({ commit }) => {
      const current = activeDragRef.current;
      stopRaf();
      if (!current) {
        return;
      }
      if (
        !commit ||
        isPlaying ||
        !frame.width ||
        !frame.height ||
        !editBounds
      ) {
        applyDragDom(restoreDragItems(current.items));
        activeDragRef.current = null;
        return;
      }
      applyDragDom(current.items);
      activeDragRef.current = null;
      commitLayouts({
        items: current.items,
        displayWidth: frame.width,
        displayHeight: frame.height,
      });
    };

    const onMove = (event) => {
      const marqueeState = marqueeRef.current;
      if (marqueeState && !activeDragRef.current) {
        const board = stageRef.current;
        if (!board) {
          return;
        }
        const point = clientToCanvasPixels(event, board);
        const next = normalizeMarqueeRect({
          originX: marqueeState.originX,
          originY: marqueeState.originY,
          x: point.x,
          y: point.y,
        });
        marqueeState.pending = next;
        if (rafRef.current) {
          return;
        }
        rafRef.current = window.requestAnimationFrame(() => {
          rafRef.current = null;
          setMarquee(marqueeState.pending || next);
        });
        return;
      }

      const current = activeDragRef.current;
      if (!current || isPlaying || !editBounds) {
        return;
      }
      const board = stageRef.current;
      if (!board) {
        return;
      }
      const point = clientToCanvasPixels(event, board);
      let nextItems = current.items;
      if (current.mode === "move") {
        const primary =
          current.items.find((item) => item.clipId === current.primaryClipId) ||
          current.items[0];
        const deltaX = point.x - current.grabOffset.x - primary.originBox.x;
        const deltaY = point.y - current.grabOffset.y - primary.originBox.y;
        nextItems = current.items.map((item) => ({
          ...item,
          box: clampPixelBoxToRect({
            x: item.originBox.x + deltaX,
            y: item.originBox.y + deltaY,
            width: item.originBox.width,
            height: item.originBox.height,
            rect: editBounds,
            minSize: MIN_BOX_SIZE_PX,
            inset: 0,
            aspectRatio: item.aspectRatio,
          }),
        }));
      } else if (current.mode === "resize") {
        nextItems = current.items.map((item) => ({
          ...item,
          box: clampPixelBoxToRect({
            ...resizePixelBoxFromHandle({
              origin: item.originBox,
              corner: current.corner,
              startPoint: current.startPoint,
              point,
              minSize: MIN_BOX_SIZE_PX,
              aspectRatio: item.aspectRatio,
            }),
            rect: editBounds,
            minSize: MIN_BOX_SIZE_PX,
            inset: 0,
            aspectRatio: item.aspectRatio,
          }),
        }));
      }
      activeDragRef.current = { ...current, items: nextItems };
      if (rafRef.current) {
        return;
      }
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        applyDragDom(activeDragRef.current?.items);
      });
    };

    const onUp = (event) => {
      const marqueeState = marqueeRef.current;
      if (marqueeState) {
        const board = stageRef.current;
        const point = board ? clientToCanvasPixels(event, board) : null;
        const rect = point
          ? normalizeMarqueeRect({
              originX: marqueeState.originX,
              originY: marqueeState.originY,
              x: point.x,
              y: point.y,
            })
          : null;
        marqueeRef.current = null;
        setMarquee(null);
        stopRaf();
        if (
          rect &&
          (rect.w >= MARQUEE_MIN_SIZE_PX || rect.h >= MARQUEE_MIN_SIZE_PX)
        ) {
          const clipIds = idsIntersectingMarquee({
            boxes: activeVisualClips.map((clip) => ({
              id: clip.id,
              ...getClipPixelBox(clip),
            })),
            marquee: rect,
            toBounds: (box) => box,
          });
          if (clipIds.length) {
            dispatch(
              setVideoEditorSelection({
                clipIds,
                mode: marqueeState.additive
                  ? VIDEO_EDITOR_SELECTION_MODE.ADD
                  : VIDEO_EDITOR_SELECTION_MODE.REPLACE,
              })
            );
          }
        }
        return;
      }

      finishActiveDrag({ commit: true });
    };

    const onCancel = () => {
      if (marqueeRef.current) {
        marqueeRef.current = null;
        setMarquee(null);
        stopRaf();
        return;
      }
      finishActiveDrag({ commit: false });
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      stopRaf();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [
    activeVisualClips,
    applyDragDom,
    commitLayouts,
    dispatch,
    editBounds,
    frame.height,
    frame.width,
    getClipPixelBox,
    isPlaying,
    stageRef,
  ]);

  useLayoutEffect(() => {
    if (activeDragRef.current) {
      return;
    }
    activeVisualClips.forEach((clip) => {
      clearDragDom(clip.id);
    });
  }, [activeVisualClips, clearDragDom, edl?.clips?.byId]);

  if (!edl) {
    return null;
  }

  return (
    <Box
      ref={overlayRef}
      onPointerDown={onOverlayPointerDown}
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "visible",
        pointerEvents: isPlaying ? "none" : "auto",
        zIndex: 5,
      }}
    >
      <Box ref={viewportRef} sx={{ position: "absolute", inset: 0 }} />
      {isBoardMeasured ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          {activeVisualClips.map((clip) => {
            const layoutStyle = getClipLayoutStyle(clip);
            if (!layoutStyle) {
              return null;
            }
            return (
              <VideoEditorVisualLayer
                key={clip.id}
                clip={clip}
                layoutStyle={layoutStyle}
                isPlaying={isPlaying}
                onMovePointerDown={onMovePointerDown}
              />
            );
          })}
        </Box>
      ) : null}
      {isBoardMeasured && editBounds && !isPlaying ? (
        <SelectionChromeOverlay
          boardRef={stageRef}
          viewportRef={viewportRef}
          selections={selections}
          syncKey={`${selectedClipIds.join(",")}-${frame.width}x${frame.height}`}
          onHandlePointerDown={onHandlePointerDown}
        />
      ) : null}
      {marquee ? <Box sx={marqueeOverlaySx(marquee)} /> : null}
    </Box>
  );
};

export default VideoEditorLayerOverlay;
